import {
  ConversationsApiCreateConversationEndpointRequest,
  ConversationsApiCreateConversationEndpointResponse,
  ConversationsApiDataConversation,
  ConversationsApiDataConversationOverview,
  ConversationsApiDataMessage,
  ConversationsApiListConversationsEndpointRequest,
  ConversationsApiListConversationsEndpointResponse,
  ConversationsApiPollConversationEndpointResponse,
  ConversationsApiRetrieveConversationEndpointResponse,
  ConversationsApiSendMessageEndpointRequest,
  ConversationsApiSendMessageEndpointResponse,
} from '../generated/types';

export const UNAUTHORIZED_ERROR = 'Unauthorized';

type SuccessResponse<T> = {
  type: 'success';
  data: T;
};

export type ErrorResponse = {
  type: 'error';
  error: string;
  code: number;
};

type ApiEndpointErrorResponse = {
  type: 'error';
  error: string;
};

type ApiEndpointSuccessResponse = {
  type: 'success';
};

type ApiEndpointResponse =
  | ApiEndpointErrorResponse
  | ApiEndpointSuccessResponse;

export type ApiResponse<T> = Promise<SuccessResponse<T> | ErrorResponse>;

export type ReportWarning = (
  error: string,
  scope?: {
    tags?: Record<string, string | number | boolean>;
  },
) => void;

type ApiMethodsOptions = {
  fetcher: (
    path: RequestInfo | URL,
    options?: RequestInit,
  ) => Promise<Response>;
  reportWarning?: ReportWarning;
  baseUrl?: string;
};

export class ApiMethods {
  static generateMessageId(): string {
    const randomString =
      globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36);
    return `incomingMessage_${randomString}`;
  }

  private fetcher: (
    path: RequestInfo | URL,
    options?: RequestInit,
  ) => Promise<Response>;

  private reportWarning?: ReportWarning;

  private baseUrl: string;

  constructor({fetcher, reportWarning, baseUrl}: ApiMethodsOptions) {
    this.fetcher = fetcher;
    this.reportWarning = reportWarning;
    this.baseUrl = baseUrl ?? '/api/v2/conversations';
  }

  private async fetch(path: string, options?: RequestInit): Promise<Response> {
    return this.fetcher(path, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
  }

  private async buildErrorResponse(
    response: Response,
    fallbackError: string,
  ): Promise<ErrorResponse> {
    try {
      const errorData = (await response.json()) as {
        error?: string | {message?: string; type?: string};
        type?: string;
      };
      if (errorData.error) {
        return {
          type: 'error',
          error:
            typeof errorData.error === 'string'
              ? errorData.error
              : errorData.error.message ?? fallbackError,
          code: response.status,
        };
      }

      this.reportWarning?.(
        'Conversations API returned a non-error body for a failed request',
        {
          tags: {
            status: response.status,
            fallback_error: fallbackError,
            response_type:
              typeof errorData.type === 'string' ? errorData.type : 'unknown',
          },
        },
      );
    } catch {
      this.reportWarning?.(
        'Conversations API returned an unparsable body for a failed request',
        {
          tags: {
            status: response.status,
            fallback_error: fallbackError,
          },
        },
      );
      return {
        type: 'error',
        error: fallbackError,
        code: response.status,
      };
    }

    return {
      type: 'error',
      error: fallbackError,
      code: response.status,
    };
  }

  private async request<TReq, TResp extends ApiEndpointResponse>(
    path: string,
    {
      method,
      body,
      fallbackError,
      notFoundError,
      parseJson = true,
      allowNoContent = false,
    }: {
      method?: RequestInit['method'];
      body?: TReq;
      fallbackError: string;
      notFoundError?: string;
      parseJson?: boolean;
      allowNoContent?: boolean;
    },
  ): Promise<Extract<TResp, ApiEndpointSuccessResponse> | ErrorResponse> {
    const init: RequestInit = {};
    if (method) {
      init.method = method;
    }
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }

    try {
      const response = await this.fetch(path, init);

      if (response.status === 401) {
        return {
          type: 'error',
          error: UNAUTHORIZED_ERROR,
          code: 401,
        };
      }

      if (response.status === 404 && notFoundError) {
        return {
          type: 'error',
          error: notFoundError,
          code: 404,
        };
      }

      if (!response.ok) {
        return this.buildErrorResponse(response, fallbackError);
      }

      if (allowNoContent && response.status === 204) {
        return {type: 'success'} as Extract<TResp, ApiEndpointSuccessResponse>;
      }

      if (!parseJson) {
        return {type: 'success'} as Extract<TResp, ApiEndpointSuccessResponse>;
      }

      const data = (await response.json()) as TResp;
      if (data.type === 'error') {
        return {
          type: 'error',
          error: data.error,
          code: response.status,
        };
      }

      return data as Extract<TResp, ApiEndpointSuccessResponse>;
    } catch (e) {
      return {
        type: 'error',
        error: (e as Error).message,
        code: 500,
      };
    }
  }

  async createConversation(
    conversation: ConversationsApiCreateConversationEndpointRequest,
  ): ApiResponse<ConversationsApiDataConversation> {
    const response = await this.request<
      ConversationsApiCreateConversationEndpointRequest,
      ConversationsApiCreateConversationEndpointResponse
    >(this.baseUrl, {
      method: 'POST',
      body: conversation,
      fallbackError: 'Failed to create conversation',
    });

    if (response.type === 'error') {
      return response;
    }

    return {
      type: 'success',
      data: response.conversation,
    };
  }

  async getConversation(
    conversationId: string,
  ): ApiResponse<ConversationsApiDataConversation> {
    const response = await this.request<
      undefined,
      ConversationsApiRetrieveConversationEndpointResponse
    >(`${this.baseUrl}/${conversationId}`, {
      fallbackError: 'Failed to get conversation',
      notFoundError: `Conversation ${conversationId} not found`,
    });

    if (response.type === 'error') {
      return response;
    }

    return {
      type: 'success',
      data: response.conversation,
    };
  }

  async listConversations({
    audience,
    entry_point: entryPoint,
    trigger_point: triggerPoint,
    status = 'active',
    limit,
  }: ConversationsApiListConversationsEndpointRequest): ApiResponse<
    ConversationsApiDataConversationOverview[]
  > {
    const query = new URLSearchParams({
      audience,
      entry_point: entryPoint,
      status,
    });
    if (triggerPoint) {
      query.set('trigger_point', triggerPoint);
    }
    if (typeof limit === 'number') {
      query.set('limit', limit.toString());
    }

    const response = await this.request<
      undefined,
      ConversationsApiListConversationsEndpointResponse
    >(`${this.baseUrl}?${query.toString()}`, {
      fallbackError: 'Failed to list conversations',
    });

    if (response.type === 'error') {
      return response;
    }

    return {
      type: 'success',
      data: response.conversations,
    };
  }

  async deleteConversation(conversationId: string): ApiResponse<undefined> {
    const response = await this.request<undefined, {type: 'success'}>(
      `${this.baseUrl}/${conversationId}`,
      {
        method: 'DELETE',
        fallbackError: 'Failed to delete conversation',
        notFoundError: `Conversation ${conversationId} not found`,
        parseJson: false,
      },
    );

    if (response.type === 'error') {
      return response;
    }

    return {
      type: 'success',
      data: undefined,
    };
  }

  async sendMessage(
    conversationId: string,
    message: ConversationsApiSendMessageEndpointRequest,
  ): ApiResponse<ConversationsApiDataConversation>;

  async sendMessage(
    conversationId: string,
    message: ConversationsApiSendMessageEndpointRequest,
    options: {allowNoContent: true},
  ): ApiResponse<ConversationsApiDataConversation | undefined>;

  async sendMessage(
    conversationId: string,
    message: ConversationsApiSendMessageEndpointRequest,
    options?: {allowNoContent?: boolean},
  ): ApiResponse<ConversationsApiDataConversation | undefined> {
    const allowNoContent = options?.allowNoContent === true;
    const response = await this.request<
      ConversationsApiSendMessageEndpointRequest,
      ConversationsApiSendMessageEndpointResponse | {type: 'success'}
    >(`${this.baseUrl}/${conversationId}/messages`, {
      method: 'POST',
      body: message,
      fallbackError: 'Failed to send message',
      allowNoContent,
    });

    if (response.type === 'error') {
      return response;
    }

    if ('conversation' in response) {
      return {
        type: 'success',
        data: response.conversation,
      };
    }

    if (allowNoContent) {
      return {
        type: 'success',
        data: undefined,
      };
    }

    this.reportWarning?.(
      'Conversations API returned unexpected sendMessage success body',
      {
        tags: {
          conversation_id: conversationId,
        },
      },
    );
    return {
      type: 'error',
      error: 'Failed to send message',
      code: 500,
    };
  }

  async pollConversation(
    conversationId: string,
  ): ApiResponse<ConversationsApiDataMessage[]> {
    const response = await this.request<
      undefined,
      ConversationsApiPollConversationEndpointResponse
    >(`${this.baseUrl}/${conversationId}/poll`, {
      fallbackError: 'Failed to poll conversation',
      notFoundError: `Conversation ${conversationId} not found`,
    });

    if (response.type === 'error') {
      return response;
    }

    return {
      type: 'success',
      data: response.messages,
    };
  }
}
